import { GameHazard } from '@/lib/types/common/GameMode';
import { getCustomDragHazards, HazardDefinition } from '@/lib/config/hazardConfig';
import { DragConfig, DynamicDragState } from './types';

/**
 * Composes drag configuration from multiple active hazards
 * Handles multipliers, inversions, and dynamic states
 */
export function composeDragConfig(
    hazards: GameHazard[],
    showHazards: boolean,
    dynamicState: DynamicDragState
): DragConfig {
    const config: DragConfig = {
        yawMultiplier: 1,
        pitchMultiplier: 1,
        yawInverted: false,
        pitchInverted: false,
        smoothFactor: undefined,
        yawDisabled: false,
        forcedAutoRotate: false,
        forcedAutoRotateSpeed: undefined,
        charmedTarget: false,
        charmedRotateSpeed: undefined,
    };

    if (!showHazards) return config;

    const customDragHazards = getCustomDragHazards(hazards);

    customDragHazards.forEach((hazard: HazardDefinition) => {
        const dragConfig = hazard.dragConfig;
        if (!dragConfig) return;

        // Apply multipliers (multiply together for stacking effects)
        if (dragConfig.yawMultiplier !== undefined) {
            config.yawMultiplier *= dragConfig.yawMultiplier;
        }
        if (dragConfig.pitchMultiplier !== undefined) {
            config.pitchMultiplier *= dragConfig.pitchMultiplier;
        }

        // Apply inversions (XOR for multiple inversions)
        if (dragConfig.yawInverted) {
            config.yawInverted = !config.yawInverted;
        }
        if (dragConfig.pitchInverted) {
            config.pitchInverted = !config.pitchInverted;
        }

        // Handle dynamic inversions (like CONFUSED hazard)
        if (dragConfig.dynamic?.enabled) {
            const currentDynamicState = dynamicState.inversions.get(hazard.id);
            if (currentDynamicState !== undefined) {
                if (dragConfig.yawInverted) {
                    config.yawInverted = currentDynamicState ? !config.yawInverted : config.yawInverted;
                }
                if (dragConfig.pitchInverted) {
                    config.pitchInverted = currentDynamicState ? !config.pitchInverted : config.pitchInverted;
                }
            }
        }
        if (dragConfig.smoothFactor !== undefined) {
            config.smoothFactor = dragConfig.smoothFactor;
        }

        // Handle yaw disabled (for FORCED_MARCH)
        if (dragConfig.yawDisabled) {
            config.yawDisabled = true;
        }

        // Handle forced autorotate (for FORCED_MARCH)
        if (dragConfig.forcedAutoRotate) {
            config.forcedAutoRotate = true;
            if (dragConfig.forcedAutoRotateSpeed !== undefined) {
                config.forcedAutoRotateSpeed = dragConfig.forcedAutoRotateSpeed;
            }
        }

        // Handle charmed target (for CHARMED)
        if (dragConfig.charmedTarget) {
            config.charmedTarget = true;
            if (dragConfig.charmedRotateSpeed !== undefined) {
                config.charmedRotateSpeed = dragConfig.charmedRotateSpeed;
            }
        }
    });

    return config;
}

/**
 * Initializes dynamic hazard behavior intervals
 */
export function initializeDynamicHazards(
    hazards: GameHazard[],
    showHazards: boolean,
    onUpdate: (state: DynamicDragState) => void
): DynamicDragState {
    const state: DynamicDragState = {
        inversions: new Map(),
        intervals: new Map(),
    };

    if (!showHazards) return state;

    const customDragHazards = getCustomDragHazards(hazards);

    customDragHazards.forEach((hazard: HazardDefinition) => {
        const dragConfig = hazard.dragConfig;
        if (dragConfig?.dynamic?.enabled && dragConfig.dynamic.interval) {
            // Initialize state
            state.inversions.set(hazard.id, false);

            // Set up interval
            const interval = setInterval(() => {
                const current = state.inversions.get(hazard.id) ?? false;
                state.inversions.set(hazard.id, !current);
                onUpdate(state);
            }, dragConfig.dynamic.interval);

            state.intervals.set(hazard.id, interval);
        }
    });

    return state;
}

/**
 * Cleans up dynamic hazard intervals
 */
export function cleanupDynamicHazards(state: DynamicDragState): void {
    state.intervals.forEach((interval) => clearInterval(interval));
    state.intervals.clear();
    state.inversions.clear();
}
